import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  IRagAvailableMeasurement,
  IRagConfig,
  IRagConfigListItem,
  RagMeasurementType,
} from "../../@types/Rag";
import { AUTH_INTERCOM_DETAILS } from "../../constants/localStorageKeys";
import { hospitalService } from "../../services/hospitalService";
import { ragConfigService } from "../../services/ragConfigService";
import { safeJsonParse, safeStorage } from "../../utils/storage";
import { useUserPreferences } from "../useUserPreferences";

export function useRagConfigList(hospitalGroupId: number | undefined) {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<
    IRagConfigListItem[]
  >({
    queryKey: ["rag-config-list", hospitalGroupId],
    queryFn: async () => {
      if (!hospitalGroupId) return [];
      const { data } = await ragConfigService.getByHospital(hospitalGroupId);
      // The api error interceptor resolves non-401 failures with the error
      // response instead of rejecting, so `data` can be an ErrorResponse
      // object (e.g. 400 RagConfigNotEnabled for hospitals without RAG —
      // EN-4999, blank patient list). Only ever surface a real array.
      return Array.isArray(data) ? data : [];
    },
    enabled: !!hospitalGroupId,
  });

  return {
    configs: data ?? [],
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

export function useRagConfigById(id: string | undefined) {
  const { data, isLoading, isError, refetch, isFetching } =
    useQuery<IRagConfig>({
      queryKey: ["rag-config", id],
      queryFn: async () => {
        if (!id) throw new Error("Missing config id");
        const { data } = await ragConfigService.getById(id);
        return data;
      },
      enabled: !!id,
    });

  return {
    ragConfig: data,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

export function useOwnHospitalGroup() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["own-hospital-group"],
    queryFn: async () => {
      const { data } = await hospitalService.getHospitalGroups();
      return data?.[0] ?? null;
    },
  });

  return {
    hospitalGroup: data,
    hospitalGroupId: data?.hospitalGroupId,
    isLoading,
    isError,
  };
}

export function useHospitalGroups(enabled: boolean = true) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["hospital-groups"],
    queryFn: async () => {
      const { data } = await hospitalService.getHospitalGroups();
      // Same ErrorResponse-as-data hazard as useRagConfigList (EN-4999).
      return Array.isArray(data) ? data : [];
    },
    enabled,
  });

  return {
    hospitalGroups: data ?? [],
    isLoading,
    isError,
  };
}

/**
 * The user's "active" hospital group (id + name). `intercomDetails` is set
 * by the backend on login and reflects the user's primary group — reliable
 * for both regular HCPs and super users, unlike picking the first item from
 * `getHospitalGroups()` which is order-dependent for super users with
 * access to many groups.
 */
export function getActiveHospitalGroup():
  | { id: number; name: string }
  | undefined {
  const intercomRaw = safeStorage.getItem(AUTH_INTERCOM_DETAILS);
  const intercomDetails = safeJsonParse<{
    hospitalGroupId?: number;
    hospitalGroupName?: string;
  } | null>(intercomRaw, null);
  const id = intercomDetails?.hospitalGroupId;
  const name = intercomDetails?.hospitalGroupName;
  if (typeof id === "number" && typeof name === "string") {
    return { id, name };
  }
  return undefined;
}

/**
 * EN-5123: the measurements that can be RAG-banded on `wards` — the union across them, with the
 * display name and the staff member's unit. Pass the config's wards in the RAG builder; pass nothing
 * (the caller's own wards) for the patient list and its config modal. Ward measurement-type config
 * changes rarely, so this is cached generously.
 */
export function useRagAvailableMeasurements(wards?: number[]) {
  // Sorted so re-ordering the ward multi-select doesn't refetch.
  const wardKey = (wards ?? []).slice().sort((a, b) => a - b);

  const { data, isLoading, isPending, isError } = useQuery<
    IRagAvailableMeasurement[]
  >({
    queryKey: ["rag-available-measurements", wardKey],
    queryFn: async () => {
      const { data } = await ragConfigService.getAvailableMeasurements(
        wardKey.length > 0 ? wardKey : undefined
      );
      // Same ErrorResponse-as-data hazard as useRagConfigList (EN-4999).
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const measurements = data ?? [];

  const byCode = useMemo(
    () => new Map(measurements.map((m) => [m.measurementType, m] as const)),
    [measurements]
  );

  // `isPending` (and not `isLoading`) is what callers should gate a first paint on: changing `wards`
  // swaps the query key, and on that first render React Query reports the new key as pending before
  // the fetch has started — `isLoading` is still false there, which would flash the loaded UI with an
  // empty measurement list.
  return { measurements, byCode, isLoading, isPending, isError };
}

/**
 * Returns which RAG measurement columns can be toggled in the patient list
 * config modal. Walks the list of configs for the user's active hospital
 * group, picks the published ones whose wards intersect with the user's
 * effective wards, fetches each by id, and unions the rules' measurement
 * types.
 */
export function useApplicableRagMeasurements() {
  const activeGroup = getActiveHospitalGroup();
  const { hospitalGroupId: fallbackId } = useOwnHospitalGroup();
  const hospitalGroupId = activeGroup?.id ?? fallbackId;
  const { configs, isLoading: isListLoading } =
    useRagConfigList(hospitalGroupId);
  const { portalPreferences } = useUserPreferences();
  const userWards = portalPreferences?.wards;

  const relevantConfigIds = useMemo(() => {
    const userWardsSet = new Set(userWards ?? []);
    // Defensive: this runs during render with server-provided data — a throw
    // here unmounts the whole app (no error boundary). Never assume shape.
    if (!Array.isArray(configs)) return [];
    return configs
      .filter((c) => {
        if (c.status !== "published") return false;
        if (userWardsSet.size === 0) return true;
        return (
          Array.isArray(c.wards) && c.wards.some((w) => userWardsSet.has(w))
        );
      })
      .map((c) => c.id);
  }, [configs, userWards]);

  const detailQueries = useQueries({
    queries: relevantConfigIds.map((id) => ({
      queryKey: ["rag-config", id],
      queryFn: async () => {
        const { data } = await ragConfigService.getById(id);
        return data;
      },
      enabled: !!id,
    })),
  });

  const isFetchingDetails = detailQueries.some((q) => q.isFetching);

  // Stable dep key: changes only when one of the underlying queries actually
  // produces new data (dataUpdatedAt bumps on success), not on every render.
  const detailUpdatedAtKey = detailQueries
    .map((q) => q.dataUpdatedAt)
    .join("|");

  const applicableMeasurements = useMemo(() => {
    // EN-5123: measurement codes ("weight", "heart_rate"), matching the RAG rules, the patient
    // list's `ragMeasurements` keys and the patient-list config JSON keys — one vocabulary, so no
    // mapping table to keep in sync as new measurements become RAG-able.
    const set = new Set<RagMeasurementType>();
    detailQueries.forEach((q) => {
      const cfg = q.data as IRagConfig | undefined;
      // `rules` can be missing when the detail call resolved with an
      // ErrorResponse body instead of a config (see useRagConfigList note).
      if (!cfg || !Array.isArray(cfg.rules)) return;
      cfg.rules.forEach((rule) => {
        if (rule.measurementType) set.add(rule.measurementType);
      });
    });
    return set;
  }, [detailUpdatedAtKey]);

  return {
    applicableMeasurements,
    isLoading: isListLoading || isFetchingDetails,
    isRagEnabled: applicableMeasurements.size > 0,
  };
}
